import React, { useRef, useCallback } from "react";
import {
    ResponsiveContainer,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar
} from "recharts";
import { Box, Button, CircularProgress } from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as htmlToImage from 'html-to-image';

interface DashboardChartProps {
    title: string;
    currentType: number;
    onTypeChange: (type: number) => void;
    isLoading: boolean;
    data: any[];
    noDataMessage?: string;
    dataKey?: string;
    barColor?: string;
    height?: number;
}

const DashboardChart: React.FC<DashboardChartProps> = ({
    title,
    currentType,
    onTypeChange,
    isLoading,
    data,
    noDataMessage = "No data available",
    dataKey = "value",
    barColor = "#C9C1B1",
    height = 300
}) => {
    const chartRef = useRef<HTMLDivElement>(null);

    const handleDownload = useCallback(() => {
        if (chartRef.current === null) {
            return;
        }

        htmlToImage.toPng(chartRef.current, { backgroundColor: '#ffffff', cacheBust: true })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-chart.png`;
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.error('oops, something went wrong!', err);
            });
    }, [title]);

    return (
        <div className="cards table_card" ref={chartRef}>
            <div className="cards_header">
                <div className="left_s">
                    <h2>{title}</h2>
                </div>
                <div className="right_s">
                    <p>
                        <span
                            className={currentType === 1 ? "active" : ""}
                            onClick={() => onTypeChange(1)}
                            style={{ cursor: 'pointer' }}
                        >
                            A day
                        </span>
                        <span
                            className={currentType === 2 ? "active" : ""}
                            onClick={() => onTypeChange(2)}
                            style={{ cursor: 'pointer' }}
                        >
                            A week
                        </span>
                        <span
                            className={currentType === 3 ? "active" : ""}
                            onClick={() => onTypeChange(3)}
                            style={{ cursor: 'pointer' }}
                        >
                            A month
                        </span>
                        <span
                            className={currentType === 4 ? "active" : ""}
                            onClick={() => onTypeChange(4)}
                            style={{ cursor: 'pointer' }}
                        >
                            A year
                        </span>
                    </p>
                    <Button
                        className="btn btn_black icon_btn"
                        onClick={handleDownload}
                        disabled={isLoading || data.length === 0}
                    >
                        <FileDownloadIcon />
                    </Button>
                </div>
            </div>
            <ResponsiveContainer width="100%" height={height}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={24} />
                    </Box>
                ) : data.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <p>{noDataMessage}</p>
                    </Box>
                ) : (
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey={dataKey} fill={barColor} />
                    </BarChart>
                )}
            </ResponsiveContainer>
        </div>
    );
};

export default DashboardChart;
